import type { ScorePage } from '../types/run-content.api.types';
import type { PreScoreRow } from '../types/run-gate.types';
import { fieldScoresAscending } from '../utils/run-score-fields';

import { curationFullUrl, curationThumbUrl } from './curation.mapper';

/** Reads a string field off a connector-shaped feed row; anything else is null. */
function str(row: Record<string, unknown>, key: string): string | null {
  const value = row[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

/** Reads a string-array field off a connector-shaped feed row. */
function strArray(row: Record<string, unknown>, key: string): string[] {
  const value = row[key];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/**
 * Maps one curation feed-product row (snake_case, connector-dependent) to a PRE
 * score row. The catalog product id lives at `product.id`; it is what content-gen
 * keys scores by. One row per FEED row at this point — `groupPreScoreRows` below
 * merges the US/UK/… feed rows of one catalog product into a single display row.
 */
export function toPreScoreRow(row: Record<string, unknown>): PreScoreRow {
  const product = row.product;
  const productId =
    product && typeof product === 'object' && 'id' in product && typeof product.id === 'string'
      ? product.id
      : null;
  const sourceId = str(row, 'source_product_id');
  return {
    feedProductId: str(row, 'feed_product_id') ?? str(row, 'id') ?? '',
    productId,
    title: str(row, 'title') ?? sourceId ?? '—',
    sourceProductIds: sourceId ? [sourceId] : [],
    // Optimized copy first — the source image_link is often a full-size storefront JPEG.
    imageUrl: curationFullUrl(row),
    thumbUrl: curationThumbUrl(row),
    gallery: strArray(row, 'additional_image_urls'),
    overall: null,
    tier: null,
    missing: true,
    missingReason: null,
    fieldScores: [],
  };
}

/**
 * Q20 — one row per catalog product: rows sharing a `productId` (the same product
 * curated into several markets, e.g. US + UK) collapse into one, unioning their
 * `sourceProductIds` so `ProductCell` can render market chips instead of
 * duplicating the product with an identical score on every market's row. A row
 * with no `productId` (unmatched) never merges with another — each keeps its own
 * feed row, keyed by `feedProductId`, so unrelated unmatched rows never collide.
 */
export function groupPreScoreRows(rows: PreScoreRow[]): PreScoreRow[] {
  const byKey = new Map<string, PreScoreRow>();
  for (const row of rows) {
    const key = row.productId ?? `unmatched:${row.feedProductId}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { ...row, sourceProductIds: [...row.sourceProductIds] });
      continue;
    }
    existing.sourceProductIds = [
      ...new Set([...existing.sourceProductIds, ...row.sourceProductIds]),
    ];
  }
  return [...byKey.values()];
}

/** Joins content-gen's PRE scores (headline, tier, per-field) onto the rows by catalog product id;
 *  an unscored row keeps `missing` and gains its R7 reason when the read sent one. */
export function withPreScores(rows: PreScoreRow[], scores: ScorePage | null): PreScoreRow[] {
  if (!scores) return rows;
  const byId = new Map(scores.products.map((p) => [p.productId, p]));
  const reasons = scores.missingReasons ?? {};
  return rows.map((row) => {
    const score = row.productId ? byId.get(row.productId) : undefined;
    if (!score) {
      const reason = row.productId ? reasons[row.productId] : undefined;
      return reason ? { ...row, missingReason: reason } : row;
    }
    const overall = score.scores.overall;
    return {
      ...row,
      overall: typeof overall === 'number' ? overall : null,
      tier: score.tier,
      missing: false,
      fieldScores: fieldScoresAscending(score.scores),
    };
  });
}
